/**
 * safeApiError -- normalise axios error objects into a small set of
 * user-safe messages.
 *
 * Why this exists:
 *   Displaying ``err.response.data.error`` verbatim is a thin layer of
 *   trust on whatever the backend put in the body. Today our handlers
 *   return generic 404 / 400 strings, but the convention is fragile --
 *   any future endpoint that includes ids, names, or other tenant data
 *   in an error body would leak it to the UI on the next failure path.
 *
 *   This helper maps known HTTP status codes (401/403/404) to fixed
 *   user-facing strings, falls back to ``err.message`` (the JS-level
 *   error string, not the server body) for everything else, and ends
 *   at a static generic message if even that is missing.
 *
 *   Surfaces that genuinely need the backend body (e.g. validation
 *   forms that surface field-level errors) should NOT use this helper
 *   -- they should reach into the typed error envelope themselves and
 *   render specific fields, not the free-form ``error`` string.
 */

const STATUS_MESSAGES = {
    401: 'You are not signed in. Please refresh and sign in again.',
    403: 'You do not have permission to perform that action.',
    404: 'That item could not be found. It may have been deleted.',
}

/**
 * @param {unknown} err - Anything thrown / rejected from an axios call.
 * @param {string} [fallback] - Final fallback if no other message is available.
 * @returns {string}
 */
export function safeApiError(err, fallback = 'Something went wrong. Please try again.') {
    if (!err) return fallback
    const status =
        err && err.response && typeof err.response.status === 'number'
            ? err.response.status
            : null
    if (status != null && STATUS_MESSAGES[status]) {
        return STATUS_MESSAGES[status]
    }
    if (err && typeof err.message === 'string' && err.message.length > 0) {
        return err.message
    }
    return fallback
}

/** Longest server message shown in place; a runaway body is cut here. */
const MAX_IN_PLACE_MESSAGE = 500

function usableBodyMessage(data) {
    if (!data || typeof data !== 'object') return null
    for (const candidate of [data.error, data.message]) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim().slice(0, MAX_IN_PLACE_MESSAGE)
        }
    }
    return null
}

/**
 * The message for a request that opts OUT of the app error toast
 * (``suppressErrorToast``, interceptors/authResponseInterceptor.js) and
 * shows its own failure once, in place -- the ColorwayEditor pattern.
 *
 * Disclosure is exactly the toast's (store/notification.js raiseError), so
 * moving the error in place shows nothing the toast did not: a 4xx body's
 * ``error`` / ``message`` is authored for the user (e.g. the aspect-validity
 * rule's ``size_aspect_mismatch`` "should be about 2.39 in tall"); a 5xx
 * body never is. 401/403/404 keep safeApiError's fixed labels.
 *
 * @param {unknown} err - Anything thrown / rejected from an axios call.
 * @param {string} [fallback] - Final fallback if no other message is available.
 * @returns {string}
 */
export function inPlaceApiError(err, fallback = 'Something went wrong. Please try again.') {
    const status =
        err && err.response && typeof err.response.status === 'number'
            ? err.response.status
            : null
    if (status != null && status >= 400 && status < 500 && !STATUS_MESSAGES[status]) {
        const message = usableBodyMessage(err.response.data)
        if (message) return message
    }
    return safeApiError(err, fallback)
}

export default safeApiError
